import axios from 'axios';
import { CLOUD_FUNCTION_URL_EUW2 } from '../../env';
import { getAuthHeaderForUser, type AuthHeader } from '../auth/auth.util';

export interface WelcomeEmailParams {
  /** First name used in the email greeting (the service requires a name). */
  name: string;
  email: string;
}

/**
 * Sends the new-user welcome / password set-up email via the standalone
 * `sendNewUserWelcomeEmail` Cloud Function (gen2, europe-west2). This replaces
 * the monolith `${CLOUD_FUNCTION_URL}/resend_welcome_email` route, which
 * `development` removed when the welcome-email flow moved into its own service.
 *
 * Used for the initial invite (Add User), single "Reissue invite", bulk resend,
 * and CSV-import invitations.
 *
 * The sign-in method (SSO vs email/password) is determined server-side by the
 * user's `tenant_domains` record, so no `isSso` flag is sent from the client.
 *
 * @param params  name / email forwarded to the service.
 * @param source  short tag for the request `metadata.source` (e.g. 'AddUser').
 */
const MAX_SEND_RETRIES = 3;
const RETRY_BASE_MS = 300;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

interface HttpishError {
  response?: { status?: number; data?: { message?: string }; headers?: Record<string, string> };
}

/**
 * The welcome function's own anti-spam 429 ("a welcome email was recently
 * sent…"). This is a legitimate application response, NOT a capacity problem —
 * it must be surfaced to the user immediately, never retried.
 */
const isCooldownRejection = (err: unknown): boolean => {
  const response = (err as HttpishError)?.response;
  return (
    response?.status === 429 &&
    /recently (sent|invited)/i.test(String(response?.data?.message ?? ''))
  );
};

/**
 * Transport-level throttling/unavailability worth a bounded retry: a capacity
 * 429 from Cloud Run (distinct from the cooldown 429 above), a 503, or a
 * network error with no HTTP response.
 */
const isRetryableSendError = (err: unknown): boolean => {
  if (isCooldownRejection(err)) return false;
  const response = (err as HttpishError)?.response;
  if (!response) return true; // no HTTP response → network error
  return response.status === 429 || response.status === 503;
};

const backoffMs = (err: unknown, attempt: number): number => {
  const retryAfter = Number((err as HttpishError)?.response?.headers?.['retry-after']);
  if (Number.isFinite(retryAfter) && retryAfter > 0) return retryAfter * 1000;
  return RETRY_BASE_MS * 2 ** attempt + Math.floor(Math.random() * 100);
};

export const sendNewUserWelcomeEmail = async (
  params: WelcomeEmailParams,
  source: string
): Promise<void> => {
  const authHeader: AuthHeader = await getAuthHeaderForUser();
  for (let attempt = 0; ; attempt += 1) {
    try {
      await axios.post(
        `${CLOUD_FUNCTION_URL_EUW2}/sendNewUserWelcomeEmail`,
        { metadata: { source }, data: { params } },
        { headers: { 'Content-Type': 'application/json', ...authHeader } }
      );
      return;
    } catch (err) {
      // Retry transient transport failures with backoff; surface the cooldown
      // 429 (and any other error) to the caller unchanged.
      if (attempt < MAX_SEND_RETRIES && isRetryableSendError(err)) {
        await sleep(backoffMs(err, attempt));
        continue;
      }
      throw err;
    }
  }
};

/** Per-bucket outcome returned by the bulk welcome endpoint. */
export interface BulkWelcomeResult {
  /** Recipients whose template bucket was accepted by SendGrid. */
  sent_emails: string[];
  /** Recipients in a bucket that failed to send — re-drivable (no cooldown armed). */
  failed_emails: string[];
  /** Recipients skipped because a prior successful invite is still within cooldown. */
  skipped_cooldown_emails: string[];
}

/**
 * Bulk variant of {@link sendNewUserWelcomeEmail}: posts the whole recipient
 * list to the `sendNewUserWelcomeEmails` endpoint, which sends ~1 request per
 * template bucket (SSO / password) instead of one call per user. This replaces
 * the legacy client-side fan-out that overran the single-send function's
 * capacity and produced HTTP 429s (smoke finding B3).
 *
 * @param users  recipients ({ name, email }); deduped server-side by email.
 * @param source short tag for the request `metadata.source`.
 */
export const sendNewUserWelcomeEmailsBulk = async (
  users: WelcomeEmailParams[],
  source: string
): Promise<BulkWelcomeResult> => {
  const authHeader: AuthHeader = await getAuthHeaderForUser();
  const response = await axios.post(
    `${CLOUD_FUNCTION_URL_EUW2}/sendNewUserWelcomeEmails`,
    { metadata: { source }, data: { params: { users } } },
    { headers: { 'Content-Type': 'application/json', ...authHeader } }
  );
  const summary = (response.data?.data ?? {}) as Partial<BulkWelcomeResult>;
  return {
    sent_emails: summary.sent_emails ?? [],
    failed_emails: summary.failed_emails ?? [],
    skipped_cooldown_emails: summary.skipped_cooldown_emails ?? []
  };
};
